import { useState } from 'react';
import { ArrowLeft, Plus, Trash2, Loader2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useTags } from '@/hooks/useTags';
import { TagBadge } from '@/components/TagBadge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useFeature } from '@/components/FeatureGate';
import { UpgradePrompt } from '@/components/UpgradePrompt';
import { cn } from '@/lib/utils';

const COLOR_OPTIONS = [
  '#22c55e', '#3b82f6', '#a855f7', '#f59e0b', '#ec4899', '#06b6d4',
  '#ef4444', '#84cc16', '#f97316', '#6366f1',
];

interface TagsSectionProps {
  onBack: () => void;
}

const FREE_TAG_LIMIT = 3;

export function TagsSection({ onBack }: TagsSectionProps) {
  const { tags, loading, addTag, updateTag, deleteTag } = useTags();
  const hasUnlimitedTags = useFeature('unlimitedTags');
  const customTagCount = tags.filter((t) => !t.is_preset).length;
  const atTagLimit = !hasUnlimitedTags && customTagCount >= FREE_TAG_LIMIT;
  const [showCreate, setShowCreate] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(COLOR_OPTIONS[0]);
  const [tagToDelete, setTagToDelete] = useState<{ id: string; name: string } | null>(null);
  const [tagToEdit, setTagToEdit] = useState<{ id: string; name: string; color: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleCreate = async () => {
    if (!newTagName.trim()) return;
    if (atTagLimit) return;
    setIsCreating(true);
    try {
      const tag = await addTag(newTagName.trim(), newTagColor);
      if (tag) {
        setNewTagName('');
        setShowCreate(false);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!tagToDelete) return;
    setIsDeleting(true);
    try {
      await deleteTag(tagToDelete.id);
      setTagToDelete(null);
      setTagToEdit(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStartEdit = (tag: { id: string; name: string; color: string }) => {
    setTagToEdit({ id: tag.id, name: tag.name, color: tag.color });
  };

  const handleSaveEdit = async () => {
    if (!tagToEdit) return;
    const trimmed = tagToEdit.name.trim();
    if (!trimmed) return;
    setIsUpdating(true);
    try {
      await updateTag(tagToEdit.id, { name: trimmed, color: tagToEdit.color });
      setTagToEdit(null);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setTagToEdit(null);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="text-lg font-semibold">Tags</h2>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Manage tags for your tasks. Add or remove tags here.
            </p>

            <div className="space-y-2">
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex flex-col gap-3 p-3 rounded-xl bg-muted/50 border border-border/50"
                >
                  {tagToEdit?.id === tag.id ? (
                    <>
                      <Input
                        value={tagToEdit.name}
                        onChange={(e) => setTagToEdit((p) => p ? { ...p, name: e.target.value } : null)}
                        placeholder="Tag name..."
                        className="h-11 text-base"
                        autoFocus
                      />
                      <div className="flex flex-wrap gap-2">
                        {COLOR_OPTIONS.map((color) => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setTagToEdit((p) => p ? { ...p, color } : null)}
                            className={cn(
                              "w-8 h-8 rounded-full transition-all",
                              tagToEdit.color === color && "ring-2 ring-offset-2 ring-offset-background ring-primary scale-110"
                            )}
                            style={{ backgroundColor: color }}
                            aria-label={`Color ${color}`}
                          />
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={handleCancelEdit}
                          disabled={isUpdating}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1"
                          onClick={handleSaveEdit}
                          disabled={!tagToEdit.name.trim() || isUpdating}
                        >
                          {isUpdating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <TagBadge name={tag.name} color={tag.color} size="md" />
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 text-muted-foreground hover:text-foreground"
                          onClick={() => handleStartEdit(tag)}
                          aria-label={`Edit ${tag.name}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 text-muted-foreground hover:text-destructive"
                          onClick={() => setTagToDelete({ id: tag.id, name: tag.name })}
                          aria-label={`Delete ${tag.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {!showCreate ? (
              atTagLimit ? (
                <UpgradePrompt
                  feature="unlimited custom tags"
                  description={`Basic includes ${FREE_TAG_LIMIT} tags. Upgrade for unlimited.`}
                  compact
                />
              ) : (
              <button
                type="button"
                onClick={() => setShowCreate(true)}
                className="w-full flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground hover:bg-accent rounded-xl transition-colors border border-dashed border-border"
              >
                <Plus className="h-4 w-4" />
                Create new tag
              </button>
              )
            ) : (
              <div className="space-y-4 p-4 rounded-xl border border-border bg-muted/30">
                <Input
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="Tag name..."
                  className="h-11 text-base"
                  autoFocus
                />
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewTagColor(color)}
                      className={cn(
                        "w-8 h-8 rounded-full transition-all",
                        newTagColor === color && "ring-2 ring-offset-2 ring-offset-background ring-primary scale-110"
                      )}
                      style={{ backgroundColor: color }}
                      aria-label={`Color ${color}`}
                    />
                  ))}
                </div>
                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 h-11"
                    onClick={() => { setShowCreate(false); setNewTagName(''); }}
                    disabled={isCreating}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    className="flex-1 h-11"
                    onClick={handleCreate}
                    disabled={!newTagName.trim() || isCreating}
                  >
                    {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!tagToDelete} onOpenChange={(open) => !open && setTagToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tag?</AlertDialogTitle>
            <AlertDialogDescription>
              {tagToDelete && `"${tagToDelete.name}" will be removed and unset from any tasks that use it.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
